import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import th from "./locales/th.json";
import en from "./locales/en.json";
import cn from "./locales/cn.json";
import mm from "./locales/mm.json";
import newgameAuth from "./locales/newgameAuth";
import newgameCoupon from "./locales/newgameCoupon";
import newgameCore from "./locales/newgameCore";
import newgameFinancial from "./locales/newgameFinancial";
import newgameLotto from "./locales/newgameLotto";
import newgamePromotionActivity from "./locales/newgamePromotionActivity";
import newgameRanking from "./locales/newgameRanking";

export const LANGUAGE_STORAGE_KEY = "lang";
export const DEFAULT_LANGUAGE = "th";
export const SUPPORTED_LANGUAGE_CODES = Object.freeze(["th", "en", "cn", "mm"]);

const HTML_LANGUAGE_CODES = Object.freeze({
  th: "th",
  en: "en",
  cn: "zh-CN",
  mm: "my",
});

const LANGUAGE_ALIASES = Object.freeze({
  th: "th",
  "th-th": "th",
  en: "en",
  "en-us": "en",
  "en-gb": "en",
  cn: "cn",
  zh: "cn",
  "zh-cn": "cn",
  "zh-hans": "cn",
  mm: "mm",
  my: "mm",
  "my-mm": "mm",
});

export const normalizeLanguage = (language) => {
  if (typeof language !== "string") return DEFAULT_LANGUAGE;

  const normalized = language.trim().toLowerCase().replaceAll("_", "-");
  return LANGUAGE_ALIASES[normalized] || DEFAULT_LANGUAGE;
};

const readStoredLanguage = () => {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;

  try {
    return normalizeLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch (_error) {
    return DEFAULT_LANGUAGE;
  }
};

const persistLanguage = (language) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch (_error) {
    // The preference is optional when storage is unavailable or blocked.
  }
};

const syncDocumentLanguage = (language) => {
  if (typeof document === "undefined") return;

  const normalized = normalizeLanguage(language);
  document.documentElement.lang = HTML_LANGUAGE_CODES[normalized];
  document.documentElement.dir = "ltr";
};

const baseResources = { th, en, cn, mm };
const resources = Object.fromEntries(
  SUPPORTED_LANGUAGE_CODES.map((language) => [
    language,
    {
      translation: {
        ...baseResources[language],
        ...newgameAuth[language],
        ...newgameCore[language],
        ng: {
          ...newgameCore[language].ng,
          ...newgameCoupon[language].ng,
          ...newgameFinancial[language].ng,
          ...newgameLotto[language].ng,
          ...newgamePromotionActivity[language].ng,
          ...newgameRanking[language].ng,
        },
      },
    },
  ]),
);

const initialLanguage = readStoredLanguage();

i18n.use(initReactI18next).init({
  resources,
  lng: initialLanguage,
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGE_CODES,
  load: "currentOnly",
  nonExplicitSupportedLngs: false,
  cleanCode: false,
  lowerCaseLng: false,
  ns: "translation",
  defaultNS: "translation",
  returnNull: false,
  initImmediate: false,
  interpolation: {
    escapeValue: false,
  },
});

syncDocumentLanguage(initialLanguage);
persistLanguage(initialLanguage);

i18n.on("languageChanged", (language) => {
  const normalized = normalizeLanguage(language);
  syncDocumentLanguage(normalized);
  persistLanguage(normalized);
});

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== LANGUAGE_STORAGE_KEY) return;

    const nextLanguage = normalizeLanguage(event.newValue);
    if (normalizeLanguage(i18n.resolvedLanguage || i18n.language) !== nextLanguage) {
      void i18n.changeLanguage(nextLanguage);
    }
  });
}

export const changeLanguage = (language) =>
  i18n.changeLanguage(normalizeLanguage(language));

export default i18n;
